import { useEffect, useMemo, useRef } from 'react';
import { formOptions } from '@tanstack/react-form';
import {
  Alert,
  Button,
  Field,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useRdCommercialRule } from '@/features/quotes/hooks/rd-equipamentos/use-rd-commercial-rule.hook';
import { useRdInstallmentConfig } from '@/features/quotes/hooks/rd-equipamentos/use-rd-installment-config.hook';
import { useRdPaymentMethods } from '@/features/quotes/hooks/rd-equipamentos/use-rd-payment-methods.hook';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useSetRdParams } from '@/features/quotes/hooks/rd-equipamentos/use-set-rd-params.hook';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import type { StepParametrosFormValues } from '@/features/quotes/schemas/rd-equipamentos/step-parametros.schema';
import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import type { RdCommercialRule } from '@/services/quotes/rd-equipamentos/types';
import {
  centsToBRL,
  factorInputToMicro,
  microToFactorDisplay,
  microToPercentDisplay,
  percentInputToMicro,
} from '@/features/quotes/utils/rd-units.util';
import { StepActions } from './sections/step-actions.component';

const formOpts = formOptions({
  defaultValues: {
    commission: '',
    discountFactor: '',
    installments: '',
    firstInstallment: '',
    paymentMethod: '',
  } as StepParametrosFormValues,
});

const FIRST_INSTALLMENT_OPTIONS = [
  { value: 'cash', label: 'À vista' },
  { value: '30d', label: 'Em 30 dias' },
];

/**
 * Aceita apenas dígitos e uma vírgula decimal — mantém o formato pt-BR
 * esperado por `percentInputToMicro`/`factorInputToMicro` (ex.: '0,60',
 * '1,15'). Diferente de `formatCurrencyInputValue`/`formatPercentInputValue`
 * (outras escalas/formatos): aqui o usuário digita o valor decimal
 * diretamente, sem máscara de milhar.
 */
function sanitizeDecimalInput(raw: string): string {
  const cleaned = raw.replace(/[^\d,]/g, '');
  const [intPart, ...rest] = cleaned.split(',');
  return rest.length ? `${intPart},${rest.join('')}` : intPart;
}

/**
 * Piso do fator de desconto/agravo (bug B2-B). O motor RD (conformado ao
 * XLS — ver `auditoria-arca-equipamentos`) trava o fator entre piso E teto
 * = exatamente 1,00 (`max_discount=0` no motor ⇒ piso = teto = FULL).
 * `RdCommercialRule` (`useRdCommercialRule()`) só expõe o TETO
 * (`maxAdjustFactor`) — não existe campo de piso na regra comercial
 * retornada pela API — então usamos aqui o mesmo valor em micro que o
 * motor aplica como piso hoje (1_000_000 = 1,00). Se a regra comercial
 * um dia expuser um campo de piso próprio (ex.: `minAdjustFactor`), trocar
 * esta constante pela leitura desse campo.
 */
const DISCOUNT_FACTOR_FLOOR_MICRO = 1_000_000;

/**
 * Erro do campo Comissão — teto de `commercialRule.maxCommission`. Função
 * pura (sem estado): chamada tanto no validator `onChange` (alimenta
 * `form.state.isValid`, usado na guarda de submit) quanto diretamente no
 * `children` do campo (garante que o texto exibido reflita sempre o valor
 * atual, sem depender de `field.state.meta.errors` — a causa do bug B2-A,
 * em que uma mensagem antiga podia ficar visível após o valor voltar a ser
 * válido).
 */
function getCommissionError(
  value: string,
  commercialRule: RdCommercialRule | undefined
): string | undefined {
  if (!commercialRule) return undefined;
  // Vazio é inválido, não "zero". Antes o vazio passava na validação e era
  // persistido como 0%, apagando em silêncio a comissão anterior; quem
  // realmente quer zero digita "0".
  if (value.trim() === '') return 'Informe a comissão.';
  const micro = percentInputToMicro(value);
  return micro > commercialRule.maxCommission
    ? 'Comissão acima do teto permitido.'
    : undefined;
}

/**
 * Erro do campo Fator de desconto/agravo — valida TETO
 * (`commercialRule.maxAdjustFactor`) E PISO (`DISCOUNT_FACTOR_FLOOR_MICRO`)
 * (bug B2-B: antes só o teto era checado no front, então um valor abaixo do
 * piso passava batido até o backend rejeitar o `PUT /params`). As duas
 * checagens são mutuamente exclusivas (teto sempre >= piso), então nunca
 * retornam a mensagem errada uma pela outra (bug B2-A). Mesma lógica de
 * "função pura" de `getCommissionError` acima.
 */
function getDiscountFactorError(
  value: string,
  commercialRule: RdCommercialRule | undefined
): string | undefined {
  if (!commercialRule) return undefined;
  // Vazio é inválido: antes furava a pré-validação do fix B2-B (que só
  // cobria valores fora do intervalo) e ainda disparava o PUT condenado a 400.
  if (value.trim() === '') return 'Informe o fator de desconto/agravo.';
  const micro = factorInputToMicro(value);
  if (micro > commercialRule.maxAdjustFactor) {
    return 'Fator de desconto/agravo acima do teto permitido.';
  }
  if (micro < DISCOUNT_FACTOR_FLOOR_MICRO) {
    return 'Fator de desconto/agravo abaixo do piso permitido.';
  }
  return undefined;
}

type KpiBoxProps = {
  label: string;
  value: string;
};

function KpiBox({ label, value }: KpiBoxProps) {
  return (
    <div className='flex flex-col gap-1 rounded-xl border border-alba-gray-400 p-4'>
      <Text className='text-xs text-alba-gray-500'>{label}</Text>
      <Text className='text-lg font-bold text-alba-black-500'>{value}</Text>
    </div>
  );
}

/**
 * Step 4 — Parâmetros (RD Equipamentos, ramo 0171): condições comerciais
 * (comissão da corretora + fator de desconto/agravo, ambos limitados pelos
 * tetos de `commercialRule`) e pagamento (parcelas + primeira
 * parcela). O cabeçalho de KPIs muda conforme o fluxo — agregado
 * (frota/lmi_unico) mostra Valor em Risco/LMI Total já definidos no Step 2;
 * individual soma o LMG (Σ LMI das coberturas selecionadas) + contagem de
 * itens.
 *
 * O submit (`useSetRdParams`) chama `PUT /params`, que já recalcula a
 * cotação no backend — o próximo step (Cotação) chega precificado, sem
 * chamada extra a `calculate`.
 */
/**
 * Coberturas que compõem o Limite Máximo de Garantia da apólice.
 *
 * Regra definida com o PO (Pedro Antonio Franceschini): o LMG é o somatório
 * de `Básica + Perda e/ou Pagamento de Aluguel + Responsabilidade Civil (RC)`.
 * A Despesa de Salvamento é calculada à parte (10% da Básica) e NÃO entra
 * no LMG.
 *
 * Antes esta tela somava TODAS as coberturas selecionadas, o que inflava o
 * LMG com a Despesa de Salvamento (QA de 2026-08-21, proposta 0171-0009);
 * depois passou a somar só Básica + Perda de Aluguel, que deixava a RC de
 * fora. A planilha e o motor (`services/pricing.py`, campo `lmg`) seguiam a
 * regra antiga: precisam ser alinhados, senão a tela contradiz a apólice.
 */
const LMG_SLUGS = ['basica', 'perda_aluguel', 'rc_maquinas'];

export function StepParametros({
  onNext,
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const form = useQuoteForm({ ...formOpts });
  const { setFieldValue } = form;

  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();
  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );
  const { data: commercialRule, isLoading: isCommercialRuleLoading } =
    useRdCommercialRule();
  const { data: installmentConfig, isLoading: isInstallmentConfigLoading } =
    useRdInstallmentConfig();
  const {
    data: paymentMethods,
    isLoading: isPaymentMethodsLoading,
    isError: isPaymentMethodsError,
  } = useRdPaymentMethods();

  const isAggregate =
    proposal?.productType === 'frota' || proposal?.productType === 'lmi_unico';

  const individualKpis = useMemo(() => {
    const equipment = proposal?.equipment ?? [];
    const lmgTotal = equipment.reduce(
      (sum, item) =>
        sum +
        item.coverages
          .filter(
            coverage =>
              coverage.selected && LMG_SLUGS.includes(coverage.coverageSlug)
          )
          .reduce(
            (coverageSum, coverage) => coverageSum + (coverage.lmi || 0),
            0
          ),
      0
    );
    return { lmgTotal, itemCount: equipment.length };
  }, [proposal]);

  const installmentOptions = useMemo(() => {
    const max = installmentConfig?.maxInstallments ?? 0;
    return Array.from({ length: max }, (_, index) => {
      const n = index + 1;
      return { value: String(n), label: `${n}x` };
    });
  }, [installmentConfig]);

  /**
   * A lista traz SÓ as formas do catálogo sincronizado do i4pro (decisão do
   * PO, 2026-09-03). A entrada "Não informada" saiu: ela não vinha do sync e
   * oferecia ao corretor emitir sem forma de pagamento, que o ERP não
   * reconhece como escolha. O estado "ainda não escolhi" continua existindo —
   * é o placeholder "Selecione", antes da primeira escolha.
   *
   * RD só oferece Boleto (decisão do PO, 2026-09-17): o catálogo do i4pro é
   * compartilhado com outros produtos e trai Cartão de Crédito, que a RD não
   * aceita. Filtro por nome, não por `code` — o código do i4pro pode mudar
   * num resync, o rótulo sincronizado sempre traz "Boleto" nele. Sabidamente
   * gambiarra (decisão consciente): o ideal seria o motor já filtrar por
   * produto, mas isso fica para outra tarefa.
   *
   * A forma JÁ GRAVADA na proposta entra na lista mesmo quando saiu do
   * catálogo (desativada no ERP, sem nome após um sync, ou — agora também —
   * filtrada por não ser Boleto). Ela É do sync — só não está mais
   * disponível para o produto — e omiti-la apagaria a escolha do corretor em
   * silêncio: sem a opção o campo exibia "Selecione", uma proposta com forma
   * escolhida parecia estar sem nenhuma, e o corretor "corrigia" o que estava
   * certo.
   */
  const paymentMethodOptions = useMemo(() => {
    const catalogo = (paymentMethods ?? [])
      .filter(method => method.name.toLowerCase().includes('boleto'))
      .map(method => ({
        value: String(method.code),
        label: 'Boleto Bancário',
      }));
    const escolhida = proposal?.paymentMethod
      ? String(proposal.paymentMethod)
      : '';
    const fora =
      escolhida && !catalogo.some(option => option.value === escolhida);
    return [
      ...catalogo,
      ...(fora
        ? [{ value: escolhida, label: `Forma ${escolhida} (indisponível)` }]
        : []),
    ];
  }, [paymentMethods, proposal?.paymentMethod]);

  const { handleSubmit, isPending, submitError } = useSetRdParams({
    proposalId: resolvedProposalId,
    form,
    onNext,
  });

  /**
   * Guarda contra o dup-PUT observado na QA: cliques rápidos podiam
   * disparar `handleSubmit` (e portanto `PUT /params`) mais de uma vez
   * antes do próprio React re-renderizar o botão com `disabled` (que só
   * reflete `isPending` um ciclo de render depois). `isSubmittingRef` é
   * síncrona — travada no exato instante do primeiro clique, então um
   * segundo clique no mesmo instante já encontra a trava ligada. Também
   * bloqueia ANTES do request se o form estiver inválido
   * (`form.state.isValid`, que reflete os validators de `commission`/
   * `discountFactor` acima) — antes, um valor acima do teto/abaixo do piso
   * ainda disparava o `PUT /params`, que só era rejeitado depois pelo
   * backend (400).
   */
  const isSubmittingRef = useRef(false);
  async function handleCalculateClick() {
    if (isSubmittingRef.current || isPending || !form.state.isValid) return;
    isSubmittingRef.current = true;
    try {
      await handleSubmit();
    } finally {
      isSubmittingRef.current = false;
    }
  }

  const hasPrefilledRef = useRef(false);
  useEffect(() => {
    if (!proposal || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;

    setFieldValue(
      'commission',
      microToPercentDisplay(proposal.commission ?? 0)
    );
    setFieldValue(
      'discountFactor',
      microToFactorDisplay(proposal.discountFactor ?? 0)
    );
    setFieldValue(
      'installments',
      proposal.installments ? String(proposal.installments) : '1'
    );
    setFieldValue('firstInstallment', proposal.firstInstallment ?? 'cash');
    setFieldValue(
      'paymentMethod',
      proposal.paymentMethod ? String(proposal.paymentMethod) : ''
    );
  }, [proposal, setFieldValue]);

  // `isPaymentMethodsLoading` NÃO entra aqui de propósito. A regra
  // comercial e o parcelamento validam campos obrigatórios, então esperar
  // por eles é necessário; o catálogo de formas de pagamento só preenche
  // um `<select>` OPCIONAL. Incluí-lo deixava o passo inteiro desabilitado
  // — inclusive comissão, parcelas e o botão de calcular — sem nenhum
  // spinner explicando por quê, já que o overlay abaixo não o observa.
  // Catálogo indisponível não pode impedir de salvar o resto.
  const blockInteraction =
    isPending ||
    isProposalLoading ||
    isCommercialRuleLoading ||
    isInstallmentConfigLoading;
  const disabled = isViewMode || blockInteraction;

  return (
    <div className='relative flex flex-col gap-8'>
      {isProposalLoading ||
      isCommercialRuleLoading ||
      isInstallmentConfigLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando dados da proposta'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando dados da proposta...
          </Text>
        </div>
      ) : null}

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        {isAggregate ? (
          <>
            <KpiBox
              label='Valor em Risco'
              value={centsToBRL(proposal?.riskValue ?? 0)}
            />
            <KpiBox
              label='LMI Total'
              value={centsToBRL(proposal?.totalLmi ?? 0)}
            />
          </>
        ) : (
          <>
            <KpiBox
              label='LMG total'
              value={centsToBRL(individualKpis.lmgTotal)}
            />
            <KpiBox
              label='Itens segurados'
              value={String(individualKpis.itemCount)}
            />
          </>
        )}
      </div>

      <div className='flex flex-col gap-6'>
        <Text className='text-base text-alba-black-500'>
          Condições comerciais
        </Text>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <form.AppField
            name='commission'
            validators={{
              onChange: ({ value }: { value: string }) =>
                getCommissionError(value, commercialRule),
            }}
            children={field => {
              const errorMessage = getCommissionError(
                field.state.value,
                commercialRule
              );
              return (
                <RequiredFieldTooltip active>
                  <Field.Root invalid={!!errorMessage}>
                    <Field.Label>
                      Comissão da corretora (%)
                      <span className='text-alba-error-red-500'>*</span>
                    </Field.Label>
                    <Field.Input
                      type='text'
                      inputMode='decimal'
                      value={field.state.value}
                      onChange={e =>
                        field.handleChange(sanitizeDecimalInput(e.target.value))
                      }
                      placeholder='0,00'
                      disabled={disabled}
                    />
                    {errorMessage ? (
                      <Field.ErrorText>{errorMessage}</Field.ErrorText>
                    ) : (
                      <Field.HelperText>
                        {commercialRule
                          ? `Teto: ${microToPercentDisplay(commercialRule.maxCommission)}%`
                          : ''}
                      </Field.HelperText>
                    )}
                  </Field.Root>
                </RequiredFieldTooltip>
              );
            }}
          />
          <form.AppField
            name='discountFactor'
            validators={{
              onChange: ({ value }: { value: string }) =>
                getDiscountFactorError(value, commercialRule),
            }}
            children={field => {
              const errorMessage = getDiscountFactorError(
                field.state.value,
                commercialRule
              );
              // Faixa degenerada: hoje o cadastro tem piso e teto iguais
              // (1,00), então o campo aceita EXATAMENTE um valor. Editável, ele
              // convidava a digitar e devolvia erro para tudo que não fosse
              // 1,00. Deriva do cadastro, não de constante: no dia em que o PO
              // liberar o agravo (`max_adjust_factor > 1`) ele volta a ser
              // editável sozinho.
              const semFaixa =
                !!commercialRule &&
                commercialRule.maxAdjustFactor <= DISCOUNT_FACTOR_FLOOR_MICRO;
              return (
                <RequiredFieldTooltip active={!semFaixa}>
                  <Field.Root invalid={!!errorMessage}>
                    <Field.Label>
                      Fator de desconto/agravo
                      <span className='text-alba-error-red-500'>*</span>
                    </Field.Label>
                    <Field.Input
                      type='text'
                      inputMode='decimal'
                      value={field.state.value}
                      onChange={e =>
                        field.handleChange(sanitizeDecimalInput(e.target.value))
                      }
                      placeholder='1,00'
                      disabled={disabled || semFaixa}
                    />
                    {errorMessage ? (
                      <Field.ErrorText>{errorMessage}</Field.ErrorText>
                    ) : (
                      <Field.HelperText>
                        {!commercialRule
                          ? ''
                          : semFaixa
                            ? `Fixo em ${microToFactorDisplay(commercialRule.maxAdjustFactor)} — sem desconto nem agravo neste produto`
                            : `Piso: ${microToFactorDisplay(DISCOUNT_FACTOR_FLOOR_MICRO)} · Teto: ${microToFactorDisplay(commercialRule.maxAdjustFactor)}`}
                      </Field.HelperText>
                    )}
                  </Field.Root>
                </RequiredFieldTooltip>
              );
            }}
          />
        </div>
      </div>

      <div className='flex flex-col gap-6'>
        <Text className='text-base text-alba-black-500'>Pagamento</Text>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <form.AppField
            name='installments'
            children={field => (
              <field.SelectField
                label='Parcelas'
                required
                options={installmentOptions}
                disabled={disabled}
              />
            )}
          />
          <form.AppField
            name='firstInstallment'
            children={field => (
              <field.RadioCardField
                label='Primeira parcela'
                options={FIRST_INSTALLMENT_OPTIONS}
                disabled={disabled}
              />
            )}
          />
          <form.AppField
            name='paymentMethod'
            children={field => (
              <field.SelectField
                label='Forma de pagamento'
                options={paymentMethodOptions}
                // O `placeholder` explica o campo desabilitado. Enquanto o
                // catálogo não chega, o rótulo da forma já escolhida ainda
                // não existe na lista, e o componente cai no placeholder —
                // com o texto padrão ("Selecione"), uma proposta que TEM
                // forma escolhida parecia estar sem nenhuma. O valor em si
                // é preservado e enviado normalmente.
                placeholder={
                  isPaymentMethodsLoading
                    ? 'Carregando formas de pagamento...'
                    : undefined
                }
                // Só ESTE campo espera o catálogo: uma escolha feita sobre
                // uma lista incompleta seria uma escolha sobre dados
                // incompletos. O resto do passo continua utilizável.
                disabled={disabled || isPaymentMethodsLoading}
              />
            )}
          />
        </div>
        {isPaymentMethodsError ? (
          <Text className='text-sm text-alba-black-400'>
            Não foi possível carregar as formas de pagamento. O campo é
            opcional: você pode seguir sem informá-lo e escolher depois.
          </Text>
        ) : null}
      </div>

      {submitError ? (
        <Alert.Root status='error'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Não foi possível salvar os parâmetros</Alert.Title>
            <Alert.Description>{submitError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        {/*
          `isValid` entra no `disabled` porque `handleCalculateClick` já
          retornava cedo quando o form era inválido (guarda do fix B2-B, que
          eliminou o PUT condenado a 400). Sem refletir isso no botão, ele
          ficava com aparência de clicável e o clique não produzia nada —
          nem toast, nem foco no campo, nem mensagem nova. Trocava um request
          condenado por um botão mudo.
        */}
        <form.Subscribe
          selector={state => state.isValid}
          children={isValid => (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              onClick={handleCalculateClick}
              disabled={isViewMode || blockInteraction || !isValid}
            >
              {isPending ? (
                <>
                  Processando...
                  <Spinner size='sm' colorScheme='gray' />
                </>
              ) : (
                <>
                  Calcular cotação
                  <ArrowRight className='h-6 w-6' strokeWidth={2} />
                </>
              )}
            </Button>
          )}
        />
      </StepActions>
    </div>
  );
}
